<template>
  <div class="campaign-family-grid margin-horizontal margin-top">
    <section class="family-section " v-for="fam in FAMILIES" :key="fam.key">
      <div class="section-header">
        <span class="section-title">{{ $t(fam.titleKey) }}</span>
        <span v-if="fam.badge" class="section-badge">{{ $t(fam.badgeKey) }}</span>
      </div>
      
      <div class="family-cards display-flex flex-direction-column gap-2">
        <f7-card 
          v-for="type in fam.types"
          :key="type.key"
          class="family-card "
          :class="{ selected: modelValue === type.key }"
          :padding="false"
          role="radio"
          :aria-checked="modelValue === type.key"
          tabindex="0"
          @click="$emit('update:modelValue', type.key)"
          @keydown.enter="$emit('update:modelValue', type.key)"
          @keydown.space.prevent="$emit('update:modelValue', type.key)"
        >
          <f7-card-content class="family-card-content" :padding="false">
            <div class="display-flex align-items-flex-start gap-3">
              <div class="card-icon flex-shrink-0">
                <f7-icon :material="type.icon" :size="20" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="card-title-row">
                  <span class="card-title">{{ typeLabel(type.key) }}</span>
                  <span v-if="type.badge" class="card-badge">{{ $t(type.badgeKey) }}</span>
                </div>
                <p class="card-description">{{ typeDesc(type.key) }}</p>
                <div v-if="type.features" class="card-features">
                  <f7-badge
                    v-for="feat in type.features"
                    :key="feat.labelKey"
                    class="feature-chip"
                    outline
                  >
                    <f7-icon :material="feat.icon" :size="12" />
                    {{ $t(feat.labelKey) }}
                  </f7-badge>
                </div>
              </div>
            </div>
          </f7-card-content>
        </f7-card>
      </div>
    </section>

    <div v-if="modelValue" class="capability-summary-card">
      <f7-icon material="info" :size="16" />
      <span>{{ capabilitySummary }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { capsFor } from '../../constants/campaign-capabilities.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({ 
  modelValue: { type: String, default: null },
  radioName: { type: String, default: 'campaign_family' }
})
defineEmits(['update:modelValue'])

const FAMILIES = [
  {
    key: 'collect',
    titleKey: 'campaign.family.collect',
    badge: true,
    badgeKey: 'campaign.family.collectBadge',
    types: [
      { key: 'FUND_COLLECT', icon: 'card_giftcard', badge: true, badgeKey: 'campaign.badge.popular', features: [{ icon: 'verified', labelKey: 'campaign.feature.instantWithdraw' }] },
      { key: 'DONATION_DRIVE', icon: 'favorite' },
      { key: 'CHARITY', icon: 'volunteer_activism' },
      { key: 'FUNDRAISER', icon: 'trending_up' },
      { key: 'COMMUNITY_PROJECT', icon: 'groups' },
      { key: 'SPONSORSHIP', icon: 'star' },
    ]
  },
  {
    key: 'savings',
    titleKey: 'campaign.family.savings',
    types: [
      { key: 'GROUP_SAVINGS', icon: 'savings', features: [{ icon: 'sync', labelKey: 'campaign.feature.autoCycle' }] },
      { key: 'TONTINE', icon: 'autorenew', features: [{ icon: 'verified_user', labelKey: 'campaign.feature.certifiedMembers' }] },
      { key: 'SUBSCRIPTION', icon: 'refresh' },
    ]
  },
  {
    key: 'commerce',
    titleKey: 'campaign.family.commerce',
    types: [
      { key: 'GROUP_BUY', icon: 'shopping_bag', features: [{ icon: 'trending_down', labelKey: 'campaign.feature.volumeDiscount' }] },
      { key: 'SAVE_TO_BUY', icon: 'shopping_cart' },
      { key: 'PREORDER', icon: 'local_shipping' },
      { key: 'COOP_PURCHASE', icon: 'storefront' },
      { key: 'PRIVATE_SHOP', icon: 'lock' },
    ]
  },
  {
    key: 'events',
    titleKey: 'campaign.family.events',
    types: [
      { key: 'TICKETING', icon: 'confirmation_number', features: [{ icon: 'qr_code', labelKey: 'campaign.feature.qrCheckin' }] },
      { key: 'EVENT_REGISTRATION', icon: 'event' },
      { key: 'RSVP', icon: 'check_circle' },
      { key: 'RAFFLE', icon: 'card_giftcard' },
    ]
  },
  {
    key: 'membership',
    titleKey: 'campaign.family.membership',
    types: [
      { key: 'MEMBERSHIP', icon: 'person_add' },
      { key: 'AUCTION', icon: 'gavel' },
    ]
  },
  {
    key: 'advertising',
    titleKey: 'campaign.family.advertising',
    types: [
      { key: 'AD_CAMPAIGN', icon: 'campaign' },
    ]
  }
]

const capabilitySummary = computed(() => {
  if (!props.modelValue) return ''
  const caps = capsFor(props.modelValue)
  const parts = []
  if (caps.contributions) parts.push(t('campaign.cap.contributions'))
  if (caps.participants) parts.push(t('campaign.cap.participants'))
  if (caps.items) parts.push(t('campaign.cap.items'))
  if (caps.cycles) parts.push(t('campaign.cap.cycles'))
  if (caps.bids) parts.push(t('campaign.cap.bids'))
  if (caps.payouts) parts.push(t('campaign.cap.payouts'))
  const label = typeLabel(props.modelValue)
  return parts.length
    ? `${label} · ${parts.join(' · ')}`
    : label
})

function typeLabel(typeKey) {
  return t(`campaign.type.${typeKey}.label`)
}
function typeDesc(typeKey) {
  return t(`campaign.type.${typeKey}.desc`)
}
</script>

<style scoped>
.campaign-family-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.family-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--f7-on-surface-variant, #8e8e93);
}

.section-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 9999px;
  background: var(--f7-theme-color);
  color: white;
  text-transform: uppercase;
}

.family-cards {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.family-card {
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: var(--avocash-surface-container-lowest, #FFFFFF);
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
  border: 1px solid var(--avocash-outline, rgba(111, 122, 113, 0.12));
  overflow: hidden;
}

.family-card:active {
  transform: scale(0.98);
}

.family-card.selected {
  background: var(--avocash-green-light, #EAF2EB);
  border: 1.5px solid var(--avocash-green, #1A6B42);
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.12), 0 1px 3px rgba(44, 52, 46, 0.08);
}

.family-card.selected .card-icon {
  background: var(--avocash-green, #1A6B42);
  color: white;
}

.family-card.selected .card-badge {
  background: white;
  color: var(--avocash-green, #1A6B42);
  border: 1.5px solid var(--avocash-green, #1A6B42);
}

.family-card-content {
  padding: 14px;
}

.card-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--f7-list-item-border-color, #e0e0e0);
  color: var(--f7-on-surface-variant, #8e8e93);
  transition: all 0.2s ease;
}



.card-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-text-color, #161d18);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 9999px;
  background: var(--f7-theme-color);
  color: white;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-description {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  line-height: 1.4;
  margin: 0 0 8px 0;
}

.card-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.feature-chip {
  font-size: 11px;
  font-weight: 500;
  color: var(--f7-theme-color);
  background: color-mix(in srgb, var(--f7-theme-color) 10%, white);
  padding: 3px 8px;
  border-radius: 9999px;
}

.family-card.selected .feature-chip {
  background: white;
  color: var(--avocash-green, #1A6B42);
  border: 1.5px solid var(--avocash-green, #1A6B42);
}

.capability-summary-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
  color: var(--f7-theme-color);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}
</style>